import {equals} from 'remeda';

type FieldType<Type> = keyof Type;
type RulesType<Type> = {[key in FieldType<Type>]: (value: string, form: Type) => string | boolean};
type ResultType<Type> = {[key in FieldType<Type>]?: string | boolean};

/**
 *  Class for validation form
 */
export class Validation<
    Type extends Record<
        string,
        {
            value: string | number;
            disabled?: boolean;
            success?: boolean;
        }
    >,
> {
    form: Type;
    rules: RulesType<Type>;

    /**
     * Write form and rules
     * @param {FormType} form - from
     * @param {RulesType} rules - rules from fields
     */
    constructor(form: Type, rules: RulesType<Type>) {
        this.rules = rules;
        this.form = form;
    }

    /**
     * Validation one field form form
     * @param {string} field - name field
     * @returns {string | boolean} - if there are no errors return false
     */
    one = (field: FieldType<Type>): string | boolean =>
        this.form[field] && this.rules[field](this.form[field].value as string, this.form);

    /**
     * Validation all fields form form
     * @returns {*} if there are no errors return false
     */
    all = (): ResultType<Type> | boolean => {
        const form: FieldType<Type>[] = Object.keys(this.form);
        const result: ResultType<Type> = {};

        if (
            form.every(
                field =>
                    this.form[field].disabled ||
                    (this.rules[field] &&
                        typeof this.rules[field](this.form[field].value as string, this.form) !==
                            'string'),
            )
        ) {
            return false;
        }

        form.forEach(value => {
            if (!this.form[value].disabled && this.rules[value]) {
                result[value] = this.rules[value](this.form[value].value as string, this.form);
            } else {
                result[value] = false;
            }
        });

        return result;
    };

    /**
     * Returns the `true` if all values after validation are equal the passed value.
     * @param {{[key: string]: string | boolean} | false} validationResult - The value that all fields should correspond to.
     * @returns {boolean} The flag, is all fields are valid.
     */
    allPass = (validationResult: {[key: string]: string | boolean} | false): boolean =>
        Object.values(this.all()).every(value => equals(validationResult, value));
}
